import type { TaskState } from '../../api/rmf-client';

/**
 * The two clocks a task row carries (sg-nomad-01, 2026-10-22). They
 * replace the single "Duration" cell, which answered three different
 * questions depending on the task's state -- a final duration, RMF's
 * remaining estimate, or an elapsed clock -- in one column, so a reader
 * could not tell which one a number was.
 *
 *   Task time            how long since the task STARTED. Ticks while it
 *                        runs; for a finished task, start to finish;
 *                        blank until it starts.
 *   Est. time remaining  RMF's estimate of the time LEFT. Counts down
 *                        while the task is active; a queued task shows
 *                        RMF's estimate of its whole duration; a dash
 *                        once it has ended.
 */

const TERMINAL: ReadonlySet<TaskState['status']> = new Set(['completed', 'failed', 'cancelled']);

export const TASK_TIME_TOOLTIP =
  'Time since the task started. A finished task shows start to finish; a task that has not started is blank.';

export const REMAINING_TOOLTIP =
  'Estimated time remaining, from the fleet manager’s own estimate. Counts down while the task runs; a queued task shows its estimated duration; a dash once it has ended.';

function startedMs(task: TaskState): number | undefined {
  if (!task.startedAt) return undefined;
  const ms = Date.parse(task.startedAt);
  return Number.isFinite(ms) ? ms : undefined;
}

/** Seconds of task time at `nowMs`, or undefined when there is none to
 *  show (queued, or ended without ever starting). */
export function taskElapsedSec(task: TaskState, nowMs: number): number | undefined {
  // Finished: the record's own start->finish, fixed.
  if (task.durationSec != null) return task.durationSec;
  // Ended with no start (cancelled while queued), or with a start but no
  // finish time: nothing honest to count.
  if (TERMINAL.has(task.status)) return undefined;
  if (task.status === 'queued') return undefined;
  const start = startedMs(task);
  if (start === undefined) return undefined;
  return Math.max(0, Math.floor((nowMs - start) / 1000));
}

export interface RemainingView {
  sec: number;
  /** `live`: an active task's remaining estimate, counting down from the
   *  moment the platform published it. `held`: an interrupted task's last
   *  estimate, not counted down -- the robot is not working through it.
   *  `duration`: a queued task's estimate of its whole run. */
  kind: 'live' | 'held' | 'duration';
}

/** The remaining-time estimate at `nowMs`, or undefined for a task that
 *  has ended or that RMF has published no estimate for. */
export function taskRemaining(task: TaskState, nowMs: number): RemainingView | undefined {
  if (TERMINAL.has(task.status)) return undefined;
  if (task.status === 'queued') {
    return task.estimatedDurationSec != null
      ? { sec: task.estimatedDurationSec, kind: 'duration' }
      : undefined;
  }
  // Loose on purpose: the wire sends `null` for "no estimate yet".
  if (task.estimatedRemainingSec == null) return undefined;
  if (task.status === 'interrupted') return { sec: task.estimatedRemainingSec, kind: 'held' };
  // The estimate is a duration as of the platform's last publish of this
  // task; count it down from there. With no publish time, show it as is.
  const sinceSec =
    task.lastUpdateMs !== undefined ? Math.max(0, (nowMs - task.lastUpdateMs) / 1000) : 0;
  return { sec: Math.max(0, Math.round(task.estimatedRemainingSec - sinceSec)), kind: 'live' };
}

/** Sort keys: a blank cell sorts below every real figure. */
export function taskElapsedSortValue(task: TaskState): number {
  return taskElapsedSec(task, Date.now()) ?? -1;
}

export function taskRemainingSortValue(task: TaskState): number {
  return taskRemaining(task, Date.now())?.sec ?? -1;
}
